// Hospitality H1 (Sep 2026) — the Host Partner (hotel/motel) portal.
//
// Read-only by design: every data call is a GET to /api/host/*, and the
// backend admits only a host token there. There is no control here that
// can edit, cancel, refund, re-dispatch or re-address an order — those
// belong to the restaurant that sold the food.
import React, { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Circle, Copy, Loader2, Lock } from "lucide-react";
import {
  clearHostToken, formatTime, formatUsd, getHostToken, hostApi, hostErrorMessage, hostLogin,
} from "../../lib/hostPortal";

const TONE_CLASSES = {
  success: "bg-emerald-100 text-emerald-800",
  active: "bg-sky-100 text-sky-800",
  pending: "bg-amber-100 text-amber-800",
  warning: "bg-rose-100 text-rose-800",
  neutral: "bg-stone-200 text-stone-700",
};

function StatusPill({ status }) {
  return (
    <span className={"inline-flex rounded-full px-2 py-0.5 text-xs font-medium " + (TONE_CLASSES[status?.tone] || TONE_CLASSES.neutral)}>
      {status?.label || "—"}
    </span>
  );
}

function useHostGet(path, params) {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  const paramsKey = JSON.stringify(params || {});
  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    hostApi.get(path, { params: JSON.parse(paramsKey) })
      .then(({ data }) => setState({ loading: false, data, error: "" }))
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          clearHostToken();
          navigate("/host/login", { replace: true });
          return;
        }
        setState({ loading: false, data: null, error: hostErrorMessage(err) });
      });
  }, [path, paramsKey, navigate]);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

function Loading() {
  return (
    <div className="py-16 grid place-items-center">
      <Loader2 className="h-6 w-6 animate-spin text-stone-400" aria-label="Loading" />
    </div>
  );
}

function ResponsibilityNote({ text }) {
  return (
    <p className="mt-8 border-t border-stone-200 pt-4 text-xs leading-relaxed text-stone-500" data-testid="host-responsibility-note">
      {text || "Food orders are sold, prepared and fulfilled by the restaurant the guest selects. Order questions, changes, cancellations and refunds are handled by that restaurant."}
    </p>
  );
}

// ------------------------------------------------------------------ layout

function HostShell({ children }) {
  const navigate = useNavigate();
  const me = useHostGet("/me");
  if (!getHostToken()) return <Navigate to="/host/login" replace />;
  const signOut = () => { clearHostToken(); navigate("/host/login", { replace: true }); };
  const tab = ({ isActive }) => "px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap "
    + (isActive ? "bg-white text-stone-900" : "text-stone-300 hover:text-white");
  return (
    <div className="min-h-screen bg-[#faf7f0] text-stone-900">
      <header className="bg-[#0d1f12] text-stone-50">
        <div className="mx-auto max-w-4xl px-4 pt-5 pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.2em] text-orange-300">Guest food orders</div>
              <h1 className="font-serif text-2xl leading-tight truncate" data-testid="host-portal-name">
                {me.data?.host?.display_name || " "}
              </h1>
            </div>
            <button type="button" onClick={signOut} className="text-sm text-stone-300 hover:text-white shrink-0" data-testid="host-sign-out">
              Sign out
            </button>
          </div>
          <nav className="mt-4 flex gap-1 overflow-x-auto" aria-label="Host portal">
            <NavLink end to="/host" className={tab}>Dashboard</NavLink>
            <NavLink to="/host/orders" className={tab}>Orders</NavLink>
            <NavLink to="/host/summary" className={tab}>Referral Summary</NavLink>
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-4 py-6">{children(me)}</main>
    </div>
  );
}

// ------------------------------------------------------------------ login

export function HostLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await hostLogin(email, password);
      navigate("/host", { replace: true });
    } catch (err) {
      setError(err?.response?.status === 401 ? "Email or password is incorrect." : hostErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen bg-[#0d1f12] grid place-items-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-[#faf7f0] p-6 text-stone-900 shadow-xl" data-testid="host-login-form">
        <div className="text-[11px] uppercase tracking-[0.2em] text-orange-600">Rudras-AI Hospitality</div>
        <h1 className="mt-1 font-serif text-2xl">Hotel partner sign in</h1>
        <label className="mt-5 block text-sm font-medium" htmlFor="host-email">Email</label>
        <input id="host-email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)}
          className="mt-1 w-full h-11 rounded-lg border border-stone-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
          data-testid="host-login-email" required />
        <label className="mt-3 block text-sm font-medium" htmlFor="host-password">Password</label>
        <input id="host-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)}
          className="mt-1 w-full h-11 rounded-lg border border-stone-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
          data-testid="host-login-password" required />
        {error && <p className="mt-3 text-sm text-rose-700" role="alert">{error}</p>}
        <button type="submit" disabled={busy} data-testid="host-login-submit"
          className="mt-5 w-full h-11 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600 disabled:opacity-60">
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

// -------------------------------------------------------------- dashboard

function Tile({ label, value, testId }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4" data-testid={testId}>
      <div className="text-3xl font-semibold tabular-nums">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-wider text-stone-500">{label}</div>
    </div>
  );
}

function GuestLink({ location }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}${location.guest_link_path}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (_e) { /* noop */ }
  };
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4" data-testid={`host-guest-link-${location.host_slug}`}>
      <div className="text-xs uppercase tracking-wider text-stone-500">Your guest ordering link{location.display_name ? ` · ${location.display_name}` : ""}</div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <code className="min-w-0 break-all rounded bg-stone-100 px-2 py-1 text-sm">{url}</code>
        <button type="button" onClick={copy} className="inline-flex items-center gap-1 rounded-lg border border-stone-300 px-3 py-1.5 text-sm hover:bg-stone-50">
          {copied ? <Check className="h-4 w-4" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
          {copied ? "Copied" : "Copy Link"}
        </button>
      </div>
      <p className="mt-2 text-xs text-stone-500">Share it in pre-arrival emails or texts, at check-in, or as a QR code in the lobby.</p>
    </div>
  );
}

export function HostDashboardPage() {
  return (
    <HostShell>
      {(me) => {
        if (me.loading && !me.data) return <Loading />;
        if (me.error) return <p className="text-rose-700">{me.error}</p>;
        const t = me.data.today;
        return (
          <div data-testid="host-dashboard">
            <h2 className="font-serif text-xl">Today</h2>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Tile label="Guest food orders" value={t.total} testId="host-tile-total" />
              <Tile label="Preparing / waiting" value={t.in_progress} testId="host-tile-in-progress" />
              <Tile label="With driver" value={t.on_the_way} testId="host-tile-on-the-way" />
              <Tile label="Delivered" value={t.delivered} testId="host-tile-delivered" />
            </div>
            <div className="mt-6 space-y-3">
              {me.data.locations.map((loc) => <GuestLink key={loc.id} location={loc} />)}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/host/orders" className="rounded-lg bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-600">Today&apos;s Orders</Link>
              <Link to="/host/summary" className="rounded-lg border border-stone-300 bg-white px-4 py-2.5 text-sm font-semibold hover:bg-stone-50">Referral Summary</Link>
            </div>
            <ResponsibilityNote text={me.data.responsibility_notice} />
          </div>
        );
      }}
    </HostShell>
  );
}

// ------------------------------------------------------------------ orders

const RANGES = [["today", "Today"], ["7d", "Last 7 days"], ["30d", "Last 30 days"]];

function OrdersBody() {
  const [range, setRange] = useState("today");
  const q = useHostGet("/orders", { range });
  const rows = q.data?.orders || [];
  return (
    <div data-testid="host-orders">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif text-xl">Guest Food Orders</h2>
        <div className="flex gap-1 rounded-lg bg-stone-200 p-1" role="tablist">
          {RANGES.map(([key, label]) => (
            <button key={key} type="button" role="tab" aria-selected={range === key} onClick={() => setRange(key)}
              className={"rounded-md px-3 py-1.5 text-sm " + (range === key ? "bg-white font-semibold shadow-sm" : "text-stone-600")}>
              {label}
            </button>
          ))}
        </div>
      </div>
      {q.loading && !q.data ? <Loading /> : q.error ? <p className="mt-4 text-rose-700">{q.error}</p> : rows.length === 0 ? (
        <p className="mt-6 text-stone-600" data-testid="host-orders-empty">No guest food orders yet for this period.</p>
      ) : (
        <div className="mt-4 overflow-hidden rounded-xl border border-stone-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-left text-xs uppercase tracking-wider text-stone-500">
              <tr>
                <th className="px-3 py-2 font-medium">Guest</th>
                <th className="px-3 py-2 font-medium hidden sm:table-cell">Restaurant</th>
                <th className="px-3 py-2 font-medium text-right">Total</th>
                <th className="px-3 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {rows.map((o) => (
                <tr key={o.order_id} className="hover:bg-orange-50/40" data-testid={`host-order-row-${o.order_code}`}>
                  <td className="px-3 py-3">
                    <Link to={`/host/orders/${o.order_id}`} className="font-medium text-stone-900 underline-offset-2 hover:underline">{o.guest_name}</Link>
                    <div className="text-xs text-stone-500">{o.order_code} · {formatTime(o.placed_at)}</div>
                    <div className="text-xs text-stone-500 sm:hidden">{o.restaurant_name}</div>
                  </td>
                  <td className="px-3 py-3 hidden sm:table-cell">{o.restaurant_name}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{formatUsd(o.total)}</td>
                  <td className="px-3 py-3"><StatusPill status={o.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ResponsibilityNote />
    </div>
  );
}

export function HostOrdersPage() {
  return <HostShell>{() => <OrdersBody />}</HostShell>;
}

function DetailBody() {
  const { orderId } = useParams();
  const q = useHostGet(`/orders/${encodeURIComponent(orderId)}`);
  if (q.loading && !q.data) return <Loading />;
  if (q.error) return <p className="text-rose-700" data-testid="host-order-not-found">{q.error}</p>;
  const o = q.data;
  const dest = o.destination || {};
  return (
    <div data-testid="host-order-detail">
      <Link to="/host/orders" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> All orders
      </Link>
      <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-stone-500">Order</div>
          <h2 className="font-serif text-2xl">{o.order_code}</h2>
          <div className="text-sm text-stone-500">{formatTime(o.placed_at)}</div>
        </div>
        <StatusPill status={o.status} />
      </div>

      <dl className="mt-5 grid grid-cols-1 gap-4 rounded-xl border border-stone-200 bg-white p-4 sm:grid-cols-2">
        <div><dt className="text-xs uppercase tracking-wider text-stone-500">Guest</dt><dd className="mt-0.5 font-medium">{o.guest_name}</dd>
          {o.guest_phone && <dd className="text-sm text-stone-600">{o.guest_phone}</dd>}</div>
        <div><dt className="text-xs uppercase tracking-wider text-stone-500">Restaurant</dt><dd className="mt-0.5 font-medium">{o.restaurant_name}</dd>
          {o.restaurant_contact_phone && <dd className="text-sm text-stone-600">{o.restaurant_contact_phone}</dd>}</div>
        <div><dt className="text-xs uppercase tracking-wider text-stone-500">Order total</dt><dd className="mt-0.5 font-medium tabular-nums">{formatUsd(o.total)}</dd></div>
        <div><dt className="text-xs uppercase tracking-wider text-stone-500">Destination</dt>
          <dd className="mt-0.5 font-medium">{dest.name} — Front Desk</dd>
          {dest.locked && <dd className="mt-0.5 inline-flex items-center gap-1 text-xs text-emerald-800"><Lock className="h-3 w-3" aria-hidden="true" />Locked to your hotel</dd>}</div>
        {o.scheduled_window_label && (
          <div><dt className="text-xs uppercase tracking-wider text-stone-500">Scheduled for</dt><dd className="mt-0.5 font-medium">{o.scheduled_window_label}</dd></div>
        )}
      </dl>

      <h3 className="mt-6 text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Status</h3>
      <ol className="mt-3 space-y-2" data-testid="host-order-timeline">
        {o.timeline.map((s) => (
          <li key={s.key} className="flex items-center gap-2 text-sm">
            {s.done
              ? <Check className="h-4 w-4 text-emerald-600" aria-hidden="true" />
              : <Circle className={"h-4 w-4 " + (s.current ? "text-orange-500 fill-orange-500" : "text-stone-300")} aria-hidden="true" />}
            <span className={s.done ? "text-stone-900" : s.current ? "font-semibold text-stone-900" : "text-stone-400"}>{s.label}</span>
          </li>
        ))}
      </ol>

      <div className="mt-6 rounded-xl border border-dashed border-stone-300 bg-stone-50 p-4" data-testid="host-front-desk-receipt">
        <div className="text-xs uppercase tracking-wider text-stone-500">Front desk</div>
        <button type="button" disabled className="mt-2 rounded-lg border border-stone-300 bg-white px-4 py-2 text-sm font-semibold text-stone-400 cursor-not-allowed">
          Food Received
        </button>
        <p className="mt-2 text-xs text-stone-500">Coming soon: confirm when the food reaches the front desk, and the guest gets a text.</p>
      </div>
      <ResponsibilityNote text={o.responsibility_notice} />
    </div>
  );
}

export function HostOrderDetailPage() {
  return <HostShell>{() => <DetailBody />}</HostShell>;
}

// ---------------------------------------------------------------- summary

function monthOptions() {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 6; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    out.push([key, d.toLocaleString("en-US", { month: "long", year: "numeric" })]);
  }
  return out;
}

function SummaryBody() {
  const options = monthOptions();
  const [month, setMonth] = useState(options[0][0]);
  const q = useHostGet("/referral-summary", { month });
  const s = q.data;
  return (
    <div data-testid="host-summary">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif text-xl">Referral Summary</h2>
        <select value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Month"
          className="h-10 rounded-lg border border-stone-300 bg-white px-3 text-sm">
          {options.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select>
      </div>
      {q.loading && !s ? <Loading /> : q.error ? <p className="mt-4 text-rose-700">{q.error}</p> : (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Tile label="Orders referred" value={s.totals.orders_referred} testId="host-summary-referred" />
            <Tile label="Completed" value={s.totals.completed_orders} testId="host-summary-completed" />
            <Tile label="Cancelled / refunded" value={s.totals.cancelled_orders} testId="host-summary-cancelled" />
            <Tile label="In progress" value={s.totals.in_progress_orders} testId="host-summary-in-progress" />
          </div>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-stone-200 bg-white p-4">
              <div className="text-xs uppercase tracking-wider text-stone-500">Referred food sales (completed)</div>
              <div className="mt-1 text-2xl font-semibold tabular-nums" data-testid="host-summary-sales">{formatUsd(s.totals.referred_food_sales_completed)}</div>
              {s.totals.partially_refunded_pending_adjustment > 0 && (
                <div className="mt-1 text-xs text-amber-800" data-testid="host-summary-pending-adjustment">
                  Partially refunded orders pending adjustment: {s.totals.partially_refunded_pending_adjustment}
                </div>
              )}
            </div>
            <div className="rounded-xl border border-orange-200 bg-orange-50 p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-orange-800">Estimated partner share</div>
              <div className="mt-1 text-2xl font-semibold tabular-nums" data-testid="host-summary-share">
                {s.totals.estimated_partner_share == null ? "Not configured" : formatUsd(s.totals.estimated_partner_share)}
              </div>
              <div className="mt-1 text-xs text-orange-900">Estimate only</div>
            </div>
          </div>

          <h3 className="mt-6 text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">By restaurant</h3>
          {s.by_restaurant.length === 0 ? (
            <p className="mt-2 text-stone-600">No referred orders this month.</p>
          ) : (
            <div className="mt-2 overflow-hidden rounded-xl border border-stone-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-left text-xs uppercase tracking-wider text-stone-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Restaurant</th>
                    <th className="px-3 py-2 font-medium text-right">Orders</th>
                    <th className="px-3 py-2 font-medium text-right hidden sm:table-cell">Completed sales</th>
                    <th className="px-3 py-2 font-medium text-right">Est. share</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {s.by_restaurant.map((r) => (
                    <tr key={r.restaurant_name}>
                      <td className="px-3 py-2.5">{r.restaurant_name}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{r.orders}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums hidden sm:table-cell">{formatUsd(r.referred_food_sales_completed)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{r.estimated_partner_share == null ? "—" : formatUsd(r.estimated_partner_share)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-4 rounded-lg bg-stone-100 p-3 text-xs leading-relaxed text-stone-600" data-testid="host-summary-disclaimer">
            <div><span className="font-semibold">Basis:</span> {s.estimate_basis.label}.</div>
            {s.partial_refund_rule && <div className="mt-1">{s.partial_refund_rule}</div>}
            <div className="mt-1">{s.disclaimer}</div>
          </div>
        </>
      )}
    </div>
  );
}

export function HostSummaryPage() {
  return <HostShell>{() => <SummaryBody />}</HostShell>;
}
